import React from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { APP_ROUTES } from '@/shared/config/routes-config';
import { UserRole } from '@/shared/enums/user-role.enum';
import { UserTeams } from '@/shared/enums/user-teams.enum';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import DefaultLayout from '@/shared/layouts/default/default.layout';

const LeadsListContainer = React.lazy(
  () => import('../../containers/leads-list/leads-list.container')
);

const LeadsAddContainer = React.lazy(
  () => import('../../containers/leads-add/leads-add.container')
);

const LeadDetailsContainer = React.lazy(
  () => import('../../containers/leads-details/leads-details.container')
);

const LeadDashboardContainer = React.lazy(
  () => import('../../containers/leads-dashboard/leads-dashboard.container')
);

const LeadsEditContainer = React.lazy(
  () => import('../../containers/leads-edit/leads-edit.container')
);

const LeadsOpsDashboardContainer = React.lazy(
  () => import('../../containers/leads-ops-dashboard/leads-ops-dashboard.container')
);

const LeadDefaultAssignmentRulesContainer = React.lazy(
  () =>
    import('../../containers/lead-default-assignment-rules/lead-default-assignment-rules.container')
);

const LeadScoringSimulatorContainer = React.lazy(
  () => import('../../containers/lead-scoring-simulator/lead-scoring-simulator.container')
);

export const LeadsRoutesConfig: RouteObject[] = [
  {
    index: true,
    element: <Navigate to={(APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH} />
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          authenticationRequired={true}
          permittedList={[
            UserRole.SALES_AGENT,
            UserRole.SALES_MANAGER,
            UserRole.SALES_QUALITY_MANAGER,
            UserRole.SALES_QUALITY_AGENT,
            UserRole.E_COMMERCE_AUTO,
            UserRole.E_COMMERCE_MANAGER,
            UserRole.MARKETING_EXECUTIVE,
            UserRole.INSURANCE_MANAGER,
            UserRole.INSURANCE_AGENT,
            UserRole.ADMIN
          ]}
          permittedTeamsList={[
            UserTeams.DIGITAL,
            UserTeams.E_COMMERCE,
            UserTeams.CORPORATE,
            UserTeams.INSURANCE
          ]}
          component={LeadsListContainer}
        />
      </React.Suspense>
    )
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).ADD.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          addLayout
          authenticationRequired={true}
          permittedList={[
            UserRole.SALES_AGENT,
            UserRole.SALES_MANAGER,
            UserRole.SALES_QUALITY_MANAGER,
            UserRole.SALES_QUALITY_AGENT,
            UserRole.E_COMMERCE_AUTO,
            UserRole.E_COMMERCE_MANAGER,
            UserRole.INSURANCE_MANAGER,
            UserRole.INSURANCE_AGENT,
            UserRole.ADMIN
          ]}
          permittedTeamsList={[
            UserTeams.DIGITAL,
            UserTeams.E_COMMERCE,
            UserTeams.CORPORATE,
            UserTeams.INSURANCE
          ]}
          component={LeadsAddContainer}
        />
      </React.Suspense>
    )
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          authenticationRequired={true}
          permittedList={[
            UserRole.SALES_AGENT,
            UserRole.SALES_MANAGER,
            UserRole.SALES_QUALITY_MANAGER,
            UserRole.SALES_QUALITY_AGENT,
            UserRole.E_COMMERCE_MANAGER,
            UserRole.E_COMMERCE_AUTO,
            UserRole.MARKETING_EXECUTIVE,
            UserRole.INSURANCE_MANAGER,
            UserRole.INSURANCE_AGENT,
            UserRole.ADMIN
          ]}
          permittedTeamsList={[
            UserTeams.DIGITAL,
            UserTeams.E_COMMERCE,
            UserTeams.CORPORATE,
            UserTeams.INSURANCE
          ]}
          component={LeadDetailsContainer}
        />
      </React.Suspense>
    )
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).DASHBOARD.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          authenticationRequired={true}
          permittedList={[
            UserRole.SALES_AGENT,
            UserRole.SALES_MANAGER,
            UserRole.SALES_QUALITY_MANAGER,
            UserRole.E_COMMERCE_MANAGER,
            UserRole.MARKETING_EXECUTIVE,
            UserRole.INSURANCE_MANAGER,
            UserRole.INSURANCE_AGENT,
            UserRole.ADMIN
          ]}
          permittedTeamsList={[
            UserTeams.DIGITAL,
            UserTeams.E_COMMERCE,
            UserTeams.CORPORATE,
            UserTeams.INSURANCE
          ]}
          component={LeadDashboardContainer}
        />
      </React.Suspense>
    )
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).OPS_DASHBOARD.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          authenticationRequired={true}
          permittedList={[
            UserRole.SALES_MANAGER,
            UserRole.SALES_QUALITY_MANAGER,
            UserRole.E_COMMERCE_MANAGER,
            UserRole.MARKETING_EXECUTIVE,
            UserRole.INSURANCE_MANAGER,
            UserRole.ADMIN
          ]}
          permittedTeamsList={[
            UserTeams.DIGITAL,
            UserTeams.E_COMMERCE,
            UserTeams.CORPORATE,
            UserTeams.INSURANCE
          ]}
          component={LeadsOpsDashboardContainer}
        />
      </React.Suspense>
    )
  },
  {
    // Lead Attribution moved under Marketing; old links and bookmarks land on the new path.
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).ATTRIBUTION_DASHBOARD.FULL_PATH,
    element: (
      <Navigate
        to={(APP_ROUTES.MARKETING_MODULE.CHILDREN as RouteConfig).LEAD_ATTRIBUTION.FULL_PATH}
        replace
      />
    )
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).DEFAULT_ASSIGNMENT_RULES.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          authenticationRequired={true}
          permittedList={[UserRole.ADMIN]}
          component={LeadDefaultAssignmentRulesContainer}
        />
      </React.Suspense>
    )
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).SCORING_SIMULATOR.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          authenticationRequired={true}
          permittedList={[
            UserRole.ADMIN,
            UserRole.SALES_MANAGER,
            UserRole.SALES_QUALITY_MANAGER,
            UserRole.MARKETING_EXECUTIVE,
            UserRole.E_COMMERCE_MANAGER
          ]}
          permittedTeamsList={[UserTeams.DIGITAL, UserTeams.E_COMMERCE]}
          component={LeadScoringSimulatorContainer}
        />
      </React.Suspense>
    )
  },
  {
    path: (APP_ROUTES.LEADS_MODULE.CHILDREN as RouteConfig).EDIT.FULL_PATH,
    element: (
      <React.Suspense fallback={<div>Loading...</div>}>
        <DefaultLayout
          addLayout
          authenticationRequired={true}
          permittedList={[
            UserRole.SALES_AGENT,
            UserRole.SALES_MANAGER,
            UserRole.SALES_QUALITY_MANAGER,
            UserRole.SALES_QUALITY_AGENT,
            UserRole.E_COMMERCE_MANAGER,
            UserRole.E_COMMERCE_AUTO,
            UserRole.INSURANCE_MANAGER,
            UserRole.INSURANCE_AGENT,
            UserRole.ADMIN
          ]}
          permittedTeamsList={[
            UserTeams.DIGITAL,
            UserTeams.E_COMMERCE,
            UserTeams.CORPORATE,
            UserTeams.INSURANCE
          ]}
          component={LeadsEditContainer}
        />
      </React.Suspense>
    )
  }
];
